import { DotLottie } from '@lottiefiles/dotlottie-web';
import { getLenis } from '../global/lenis.js';

const SELECTORS = {
    wrapper: '.scroll-animation-wrapper',
    canvas: '.scroll-animation-canvas',
};

function parseSpriteUrls(raw) {
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);

        return Array.isArray(parsed) ? parsed.filter((u) => typeof u === 'string' && u) : [];
    } catch {
        return [];
    }
}

// Shared scroll scrubber: maps wrapper scroll progress to a damped frame
// index and calls `onDraw(frame)` when the rounded frame changes. Handles
// lazy init via IntersectionObserver and pauses the listener while the
// block is off-screen. `onInit` runs once when the block enters the
// extended viewport; resolving its promise enables scroll-driven draws.
function createScrollScrubber({ wrapper, getMaxFrame, onDraw, onInit, onResize }) {
    const DAMPING = 0.18;
    const EPSILON = 0.01;
    let targetIndex = 0;
    let currentIndex = 0;
    let lastDrawnFrame = -1;
    let tickId = null;
    let resizeRafId = null;
    let scrollListenerAttached = false;
    let ready = false;

    const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

    const drawIfChanged = () => {
        if (!ready) return;
        const frame = Math.round(currentIndex);
        if (frame !== lastDrawnFrame) {
            onDraw(frame);
            lastDrawnFrame = frame;
        }
    };

    const tick = () => {
        const diff = targetIndex - currentIndex;
        if (Math.abs(diff) < EPSILON) {
            currentIndex = targetIndex;
            drawIfChanged();
            tickId = null;

            return;
        }
        currentIndex += diff * DAMPING;
        drawIfChanged();
        tickId = requestAnimationFrame(tick);
    };

    const requestTick = () => {
        if (tickId == null) tickId = requestAnimationFrame(tick);
    };

    // Read wrapper geometry fresh on every update — caching `wrapperTop` was
    // brittle against any layout shift above the wrapper (lazy images, font
    // swaps, deferred blocks) which silently desynced the scroll-to-frame math.
    const updateTarget = () => {
        const rect = wrapper.getBoundingClientRect();
        const scrollRange = rect.height - window.innerHeight;
        const maxFrame = getMaxFrame();
        if (scrollRange <= 0 || maxFrame <= 0) {
            targetIndex = 0;

            return;
        }
        const progress = clamp(-rect.top / scrollRange, 0, 1);
        targetIndex = progress * maxFrame;
    };

    const onScroll = () => {
        updateTarget();
        requestTick();
    };

    const snapToScroll = () => {
        updateTarget();
        currentIndex = targetIndex;
        drawIfChanged();
    };

    const attachScroll = () => {
        if (scrollListenerAttached) return;
        const lenis = getLenis();
        if (lenis) lenis.on('scroll', onScroll);
        else window.addEventListener('scroll', onScroll, { passive: true });
        scrollListenerAttached = true;
        snapToScroll();
    };

    const detachScroll = () => {
        if (scrollListenerAttached) {
            const lenis = getLenis();
            if (lenis) lenis.off('scroll', onScroll);
            else window.removeEventListener('scroll', onScroll);
            scrollListenerAttached = false;
        }
        if (tickId != null) {
            cancelAnimationFrame(tickId);
            tickId = null;
        }
    };

    const handleResize = () => {
        if (resizeRafId != null) cancelAnimationFrame(resizeRafId);
        resizeRafId = requestAnimationFrame(() => {
            resizeRafId = null;
            if (onResize) onResize();
            if (ready) {
                // Force a redraw — pixels are stale after layout change.
                lastDrawnFrame = -1;
                snapToScroll();
            }
        });
    };

    window.addEventListener('resize', handleResize, { passive: true });
    window.addEventListener('orientationchange', handleResize, { passive: true });

    const beginPlayback = () => {
        ready = true;
        attachScroll();
    };

    const initialize = () => {
        Promise.resolve(onInit ? onInit() : undefined).then(beginPlayback);
    };

    if (typeof IntersectionObserver === 'undefined') {
        initialize();

        return;
    }

    const loadObserver = new IntersectionObserver(
        (entries) => {
            if (entries[0].isIntersecting) {
                loadObserver.disconnect();
                initialize();
            }
        },
        { rootMargin: '300px' },
    );

    const cleanupObserver = new IntersectionObserver(
        (entries) => {
            if (!entries[0].isIntersecting) detachScroll();
            else if (ready) attachScroll();
        },
        { rootMargin: '100%' },
    );

    loadObserver.observe(wrapper);
    cleanupObserver.observe(wrapper);
}

function initSpriteAnimation(wrapper) {
    const spriteUrls = parseSpriteUrls(wrapper.dataset.spriteUrls);
    const frameCount = parseInt(wrapper.dataset.frameCount, 10);
    const framesPerSprite = parseInt(wrapper.dataset.framesPerSprite, 10);
    const framesPerRow = parseInt(wrapper.dataset.framesPerRow, 10);
    const frameWidth = parseInt(wrapper.dataset.frameWidth, 10);
    const frameHeight = parseInt(wrapper.dataset.frameHeight, 10);

    if (!spriteUrls.length || !frameCount || !framesPerSprite || !framesPerRow || !frameWidth || !frameHeight) {
        console.warn('[scroll-animation] missing required data attributes, aborting');

        return;
    }

    const canvas = wrapper.querySelector(SELECTORS.canvas);
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const sprites = spriteUrls.map(() => new Image());
    let dw = 0;
    let dh = 0;
    // Derived from sprite[0].naturalWidth once decoded. Defensive — if anything
    // rescales the sprite after upload (e.g. WP big_image_size_threshold) the
    // cell math stays correct because we measure the real image.
    let cellW = frameWidth;
    let cellH = frameHeight;

    const sizeCanvas = () => {
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        dw = canvas.offsetWidth;
        dh = window.innerHeight;
        canvas.width = dw * dpr;
        canvas.height = dh * dpr;
        ctx.scale(dpr, dpr);
        ctx.imageSmoothingQuality = 'low';
    };

    const drawFrame = (index) => {
        const spriteIndex = Math.floor(index / framesPerSprite);
        const localIndex = index % framesPerSprite;
        const col = localIndex % framesPerRow;
        const row = Math.floor(localIndex / framesPerRow);
        const sprite = sprites[spriteIndex];
        if (!sprite || !sprite.complete || !sprite.naturalWidth) return;

        // "Contain" fit — preserve frame aspect, fit fully inside the canvas.
        const srcAspect = cellW / cellH;
        const dstAspect = dw / dh;
        let drawW;
        let drawH;
        let drawX;
        let drawY;
        if (srcAspect > dstAspect) {
            drawW = dw;
            drawH = dw / srcAspect;
            drawX = 0;
            drawY = (dh - drawH) / 2;
        } else {
            drawH = dh;
            drawW = dh * srcAspect;
            drawX = (dw - drawW) / 2;
            drawY = 0;
        }

        ctx.clearRect(0, 0, dw, dh);
        ctx.drawImage(sprite, col * cellW, row * cellH, cellW, cellH, drawX, drawY, drawW, drawH);
    };

    const loadAllSprites = () =>
        Promise.all(
            sprites.map(
                (sprite, i) =>
                    new Promise((resolve) => {
                        sprite.onload = () => {
                            sprite
                                .decode()
                                .then(() => resolve())
                                .catch(() => resolve());
                        };
                        sprite.onerror = () => resolve();
                        sprite.src = spriteUrls[i];
                    }),
            ),
        );

    createScrollScrubber({
        wrapper,
        getMaxFrame: () => frameCount - 1,
        onInit: async () => {
            sizeCanvas();
            await loadAllSprites();
            const first = sprites[0];
            if (first && first.naturalWidth) {
                cellW = first.naturalWidth / framesPerRow;
                const firstSpriteRows = Math.min(Math.ceil(frameCount / framesPerRow), framesPerSprite / framesPerRow);
                cellH = first.naturalHeight / firstSpriteRows;
            }
        },
        onDraw: drawFrame,
        onResize: sizeCanvas,
    });
}

function initLottieAnimation(wrapper) {
    const lottieUrl = wrapper.dataset.lottieUrl;
    if (!lottieUrl) {
        console.warn('[scroll-animation] missing lottie URL, aborting');

        return;
    }
    const canvas = wrapper.querySelector(SELECTORS.canvas);
    if (!canvas) return;

    let player = null;
    let totalFrames = 0;

    // dotlottie-web rasterizes into the canvas's pixel buffer (canvas.width /
    // canvas.height) — left at default that's 300×150, which gets upscaled by
    // CSS to the displayed size and looks blurry. Match the backing store to
    // the displayed CSS box × DPR so the renderer paints at native resolution.
    const sizeCanvas = () => {
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const dw = canvas.offsetWidth;
        const dh = window.innerHeight;
        if (!dw || !dh) return;
        canvas.width = dw * dpr;
        canvas.height = dh * dpr;
    };

    createScrollScrubber({
        wrapper,
        getMaxFrame: () => (totalFrames > 0 ? totalFrames - 1 : 0),
        onInit: () =>
            new Promise((resolve) => {
                sizeCanvas();
                player = new DotLottie({
                    canvas,
                    src: lottieUrl,
                    autoplay: false,
                    loop: false,
                });
                const onLoad = () => {
                    totalFrames = player.totalFrames || 0;
                    resolve();
                };
                const onError = () => {
                    console.warn('[scroll-animation] failed to load lottie', lottieUrl);
                    resolve();
                };
                player.addEventListener('load', onLoad);
                player.addEventListener('loadError', onError);
            }),
        onDraw: (frame) => {
            if (player) player.setFrame(frame);
        },
        onResize: () => {
            sizeCanvas();
            if (player && typeof player.resize === 'function') player.resize();
        },
    });
}

// Mobile fallback: a smaller auto-playing, looping video that lazy-loads
// (src only set when the wrapper enters the viewport) and pauses while
// off-screen. Used when the device matches the mobile breakpoint and a
// mobile video has been configured for the block.
function initMobileVideoPlayback(wrapper, mobileVideoUrl) {
    const mobileVideo = wrapper.querySelector('.scroll-animation-video-mobile');
    const desktopVideo = wrapper.querySelector('.scroll-animation-video');
    const canvas = wrapper.querySelector(SELECTORS.canvas);
    if (!mobileVideo) {
        console.warn('[scroll-animation] mobile video element missing, aborting');

        return;
    }

    // Hide the desktop scrub elements; show the mobile video. Clear the
    // wrapper's `height: {scrollHeight}vh` so it collapses to the video's
    // natural aspect-ratio height (no need for scroll-scrub runway on mobile).
    if (canvas) canvas.style.display = 'none';
    if (desktopVideo) desktopVideo.style.display = 'none';
    wrapper.style.height = 'auto';
    mobileVideo.style.display = 'block';

    let srcLoaded = false;
    let isVisible = false;

    const ensureSrc = () => {
        if (srcLoaded) return;
        mobileVideo.src = mobileVideoUrl;
        mobileVideo.load();
        srcLoaded = true;
    };

    const handleVisibility = (visible) => {
        isVisible = visible;
        if (visible) {
            ensureSrc();
            const playPromise = mobileVideo.play();
            // play() can reject on some browsers if the element is removed
            // before the promise settles — swallow it to avoid console noise.
            if (playPromise && typeof playPromise.catch === 'function') {
                playPromise.catch(() => { /* no-op */ });
            }
        } else if (srcLoaded) {
            mobileVideo.pause();
        }
    };

    if (typeof IntersectionObserver === 'undefined') {
        // No observer support → load and play immediately. Conservative
        // fallback that matches pre-lazy-loading browser behavior.
        handleVisibility(true);

        return;
    }

    const observer = new IntersectionObserver(
        (entries) => {
            handleVisibility(entries[0].isIntersecting);
        },
        // 200px head-start so the first frame is decoded before it scrolls
        // fully into view, avoiding a black flash on entry.
        { rootMargin: '200px' },
    );
    observer.observe(wrapper);

    // Some mobile browsers pause the video when the tab loses focus and
    // don't resume on focus return — re-check visibility on visibilitychange.
    document.addEventListener('visibilitychange', () => {
        if (!document.hidden && isVisible && srcLoaded) {
            const playPromise = mobileVideo.play();
            if (playPromise && typeof playPromise.catch === 'function') {
                playPromise.catch(() => { /* no-op */ });
            }
        }
    });
}

const MOBILE_QUERY = '(max-width: 768px)';

function initVideoAnimation(wrapper) {
    const canvas = wrapper.querySelector(SELECTORS.canvas);
    const video = wrapper.querySelector('.scroll-animation-video');
    if (!canvas || !video || !video.getAttribute('src')) {
        console.warn('[scroll-animation] missing video element/src, aborting');

        return;
    }

    const ctx = canvas.getContext('2d');
    let dw = 0;
    let dh = 0;
    let duration = 0;
    let videoW = 0;
    let videoH = 0;
    // Virtual frame granularity for the scrubber — it works in integer frame
    // indices, so we discretize the video timeline at 60 ticks/sec. Finer
    // than source fps so damping has somewhere to ease.
    const VIRTUAL_FPS = 60;
    let virtualFrames = 0;
    let lastSeekTime = -1;
    let paintRafId = null;

    const sizeCanvas = () => {
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        dw = canvas.offsetWidth;
        dh = window.innerHeight;
        canvas.width = dw * dpr;
        canvas.height = dh * dpr;
        ctx.scale(dpr, dpr);
        ctx.imageSmoothingQuality = 'low';
    };

    const drawVideo = () => {
        if (!videoW || !videoH || !dw || !dh) return;
        // "Contain" fit — preserve video aspect, fit fully inside the canvas.
        const srcAspect = videoW / videoH;
        const dstAspect = dw / dh;
        let drawW;
        let drawH;
        let drawX;
        let drawY;
        if (srcAspect > dstAspect) {
            drawW = dw;
            drawH = dw / srcAspect;
            drawX = 0;
            drawY = (dh - drawH) / 2;
        } else {
            drawH = dh;
            drawW = dh * srcAspect;
            drawX = (dw - drawW) / 2;
            drawY = 0;
        }
        ctx.clearRect(0, 0, dw, dh);
        ctx.drawImage(video, drawX, drawY, drawW, drawH);
    };

    // video.currentTime updates are asynchronous, so onDraw can't synchronously
    // paint the new frame. The rAF loop keeps the canvas in sync with the
    // video element's current decoded frame. drawImage(video, …) is GPU-cheap.
    const startPaintLoop = () => {
        if (paintRafId != null) return;
        const loop = () => {
            drawVideo();
            paintRafId = requestAnimationFrame(loop);
        };
        paintRafId = requestAnimationFrame(loop);
    };

    const stopPaintLoop = () => {
        if (paintRafId != null) {
            cancelAnimationFrame(paintRafId);
            paintRafId = null;
        }
    };

    if (typeof IntersectionObserver !== 'undefined') {
        const paintObserver = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) startPaintLoop();
                else stopPaintLoop();
            },
            { rootMargin: '100%' },
        );
        paintObserver.observe(wrapper);
    }

    createScrollScrubber({
        wrapper,
        getMaxFrame: () => (virtualFrames > 0 ? virtualFrames - 1 : 0),
        onInit: () =>
            new Promise((resolve) => {
                sizeCanvas();
                const onMeta = () => {
                    duration = video.duration || 0;
                    videoW = video.videoWidth || 0;
                    videoH = video.videoHeight || 0;
                    virtualFrames = Math.max(1, Math.round(duration * VIRTUAL_FPS));
                    try { video.currentTime = 0 } catch { /* no-op */ }
                    startPaintLoop();
                    resolve();
                };
                const onError = () => {
                    console.warn('[scroll-animation] failed to load video', video.src);
                    resolve();
                };
                if (video.readyState >= 1) {
                    onMeta();

                    return;
                }
                video.addEventListener('loadedmetadata', onMeta, { once: true });
                video.addEventListener('error', onError, { once: true });
            }),
        onDraw: (frame) => {
            if (!duration || virtualFrames <= 1) return;
            const t = (frame / (virtualFrames - 1)) * duration;
            // Skip redundant currentTime sets — repeated sets to the same
            // value still cost the decoder a re-seek on some browsers.
            if (Math.abs(t - lastSeekTime) > 1 / VIRTUAL_FPS / 2) {
                lastSeekTime = t;
                try { video.currentTime = t } catch { /* no-op */ }
            }
        },
        onResize: sizeCanvas,
    });
}

function initScrollAnimation(wrapper) {
    // Mobile path overrides every source: if the device matches the mobile
    // breakpoint AND a mobile fallback video is configured, skip the heavier
    // sprite / lottie / scrub pipelines and play the lightweight looping
    // video instead.
    const mobileVideoUrl = wrapper.dataset.mobileVideoUrl;
    if (window.matchMedia(MOBILE_QUERY).matches) {
        if (mobileVideoUrl) {
            initMobileVideoPlayback(wrapper, mobileVideoUrl);
        } else {
            wrapper.style.display = 'none';
        }

        return;
    }

    const source = wrapper.dataset.source || 'vimeo';
    if (source === 'lottie') {
        initLottieAnimation(wrapper);
    } else if (source === 'video') {
        initVideoAnimation(wrapper);
    } else {
        initSpriteAnimation(wrapper);
    }
}

export function ScrollAnimation() {
    document.querySelectorAll(SELECTORS.wrapper).forEach((wrapper) => {
        initScrollAnimation(wrapper);
    });
}
